<template>
  <div v-if="visible" class="print-window-overlay">
    <div class="print-window" @click.stop>
      <!-- Header con botones de acción -->
      <div class="print-header">
        <div class="header-title">
          <i class="fas fa-receipt"></i>
          Comprobante #{{ saleData.idTransaccion }}
        </div>
        <div class="header-actions">
          <!-- Selector de formato -->
          <div class="format-selector">
            <label class="format-label">Formato:</label>
            <select v-model="selectedFormat" @change="updateFormat" class="format-select">
              <option value="thermal">Térmico 80mm</option>
              <option value="letter">Carta</option>
            </select>
          </div>
          
          <button 
            @click="downloadPDF" 
            class="btn-action pdf"
            type="button"
            :style="{ opacity: 1, cursor: 'pointer' }"
          >
            <i class="fas fa-file-pdf"></i>
            <span>Generar PDF {{ formatTypeText }}</span>
          </button>
          <button 
            @click="closeModal" 
            class="btn-action close"
            type="button"
            :style="{ opacity: 1, cursor: 'pointer' }"
          >
            <i class="fas fa-times"></i>
            <span>Cerrar</span>
          </button>
        </div>
      </div>

      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-content">
          <i class="fas fa-spinner fa-spin"></i>
          {{ loadingMessage }}
        </div>
      </div>

      <!-- Contenido del comprobante -->
      <div class="print-content" ref="printContent">
        <div class="voucher-container" :class="{ 'thermal-format': isThermalFormat, 'letter-format': isLetterFormat }">
          <!-- Header del comprobante -->
          <div class="voucher-header">
            <div class="company-info">
              <h2>{{ companyData.name }}</h2>
              <p>RUT: {{ companyData.rut }}</p>
              <p>{{ companyData.address }}</p>
              <p>{{ companyData.contact }}</p>
            </div>
            <div class="document-info">
              <h3>COMPROBANTE DE VENTA</h3>
              <p>N° {{ saleData.idTransaccion }}</p>
              <p>{{ formatDate(saleData.fechaHora) }}</p>
            </div>
          </div>

          <!-- Información de la venta -->
          <div class="sale-info">
            <div class="info-row">
              <span class="label">Cliente:</span>
              <span class="value">{{ saleData.nombreCliente || 'Cliente General' }}</span>
            </div>
            <div class="info-row" v-if="saleData.rutCliente">
              <span class="label">RUT:</span>
              <span class="value">{{ saleData.rutCliente }}</span>
            </div>
            <div class="info-row">
              <span class="label">Vendedor:</span>
              <span class="value">{{ saleData.nombreVendedor || 'Sistema' }}</span>
            </div>
            <!-- Método de pago simple -->
            <div v-if="!isMultiPago" class="info-row">
              <span class="label">Método de Pago:</span>
              <span class="value">{{ saleData.metodoPago || 'No especificado' }}</span>
            </div>
            
            <!-- Métodos de pago múltiples -->
            <div v-else class="payment-methods-section">
              <div class="info-row">
                <span class="label">Método de Pago:</span>
                <span class="value">[MULTIPAGO]</span>
              </div>
              
              <!-- Formato térmico para multipago -->
              <div v-if="isThermalFormat" class="thermal-payments">
                <div class="thermal-payment-header">
                  <span class="payment-method-col">Forma de Pago</span>
                  <span class="payment-amount-col">Monto</span>
                </div>
                <div v-for="(payment, index) in paymentMethods" :key="index" class="thermal-payment-row">
                  <span class="payment-method">{{ payment.method }}</span>
                  <span class="payment-amount">{{ formatCurrency(payment.amount) }}</span>
                </div>
              </div>
              
              <!-- Formato carta para multipago -->
              <div v-else class="letter-payments">
                <table class="payment-methods-table">
                  <thead>
                    <tr>
                      <th>Forma de Pago</th>
                      <th>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(payment, index) in paymentMethods" :key="index">
                      <td>{{ payment.method }}</td>
                      <td>{{ formatCurrency(payment.amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Detalle de items -->
          <div class="items-section">
            <h4>DETALLE DE LA VENTA</h4>
            
            <!-- Tabla formato térmico -->
            <div v-if="isThermalFormat" class="thermal-items">
              <div class="thermal-header">
                <span class="th-codigo">Código</span>
                <span class="th-cant">Cant.</span>
                <span class="th-precio">$ Unit.</span>
                <span class="th-total">$ Total</span>
              </div>
              
              <div v-for="(item, index) in processedItems" :key="index" class="thermal-item">
                <!-- Primera línea: datos numéricos -->
                <div class="thermal-row-data">
                  <span class="td-codigo">{{ item.codigo }}</span>
                  <span class="td-cant">{{ item.cantidad }}</span>
                  <span class="td-precio">{{ formatCurrency(item.precioUnitario) }}</span>
                  <span class="td-total">{{ formatCurrency(item.total) }}</span>
                </div>
                <!-- Segunda línea: descripción -->
                <div class="thermal-row-description">
                  {{ item.descripcion }}
                </div>
              </div>
            </div>
            
            <!-- Tabla formato carta tradicional -->
            <table v-else class="items-table letter-format">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th>Cant.</th>
                  <th>Precio Unit.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in processedItems" :key="index">
                  <td>{{ item.codigo }}</td>
                  <td>{{ item.descripcion }}</td>
                  <td>{{ item.cantidad }}</td>
                  <td>{{ formatCurrency(item.precioUnitario) }}</td>
                  <td>{{ formatCurrency(item.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Totales -->
          <div class="totals-section">
            <div class="total-row subtotal">
              <span class="label">Neto:</span>
              <span class="value">{{ formatCurrency(calculatedTotals.neto) }}</span>
            </div>
            <div class="total-row tax">
              <span class="label">IVA (19%):</span>
              <span class="value">{{ formatCurrency(calculatedTotals.iva) }}</span>
            </div>
            <div class="total-row final">
              <span class="label">TOTAL:</span>
              <span class="value">{{ formatCurrency(calculatedTotals.total) }}</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="voucher-footer">
            <p>Estado: {{ getStatusText(saleData.estadoVenta) }}</p>
            <p>Documento generado el {{ formatDate(new Date()) }}</p>
            <p>Sistema CentralGym - www.2tec.cl</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VoucherPrintModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    saleData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      loadingMessage: '',
      selectedFormat: 'thermal', // Térmico por defecto
      companyData: {
        name: 'CentralGym Spa',
        rut: '77.935.821-6',
        address: 'Av. Club Hípico #4476, Piso 7, Torre 6, Pedro Aguirre Cerda, Santiago, RM',
        contact: 'Tel: (+56) 9 6438 2609 - www.2tec.cl'
      }
    }
  },
  computed: {
    processedItems() {
      const items = [];
      
      // Procesar items del detalle de venta
      if (this.saleData.infoVenta?.detalleVenta?.length > 0) {
        this.saleData.infoVenta.detalleVenta.forEach((item, index) => {
          items.push({
            codigo: item.codigo || `0100${index + 1}`,
            descripcion: item.detalle || item.descripcion || 'Producto/Servicio',
            cantidad: parseInt(item.cantidad || 1),
            precioUnitario: parseFloat(item.precioUnitarioBruto || item.precio || 0),
            total: parseFloat(item.precio || item.totalNeto || 0)
          });
        });
      }
      
      // Si no hay items específicos, crear uno genérico
      if (items.length === 0) {
        items.push({
          codigo: '01001',
          descripcion: 'Venta de Gimnasio',
          cantidad: 1,
          precioUnitario: parseFloat(this.saleData.ventaTotal || 0),
          total: parseFloat(this.saleData.ventaTotal || 0)
        });
      }
      
      return items;
    },
    
    calculatedTotals() {
      const total = parseFloat(this.saleData.ventaTotal || 0);
      
      // CORREGIDO: Aplicar fórmulas correctas para cálculo de IVA
      // Valor Neto = Total Venta / 1.19
      const neto = Math.round(total / 1.19);
      
      // IVA = Valor Neto * 19%
      const iva = Math.round(neto * 0.19);
      
      // Verificación: Valor Total = Valor Neto + IVA (debe coincidir con total original)
      const calculatedTotal = neto + iva;
      
      console.log('💰 Cálculos de IVA:', {
        totalOriginal: total,
        netoCalculado: neto,
        ivaCalculado: iva,
        totalCalculado: calculatedTotal,
        diferencia: Math.abs(total - calculatedTotal),
        formula: 'Neto = Total/1.19, IVA = Neto*0.19'
      });
      
      return {
        neto,
        iva,
        total
      };
    },
    
    formatTypeText() {
      return this.selectedFormat === 'thermal' ? 'Térmico' : 'Carta';
    },
    
    isThermalFormat() {
      return this.selectedFormat === 'thermal';
    },
    
    isLetterFormat() {
      return this.selectedFormat === 'letter';
    },
    
    isMultiPago() {
      return this.saleData.metodoPago === '[MULTIPAGO]';
    },
    
    paymentMethods() {
      if (!this.isMultiPago || !this.saleData.infoVenta?.venta) {
        return [];
      }
      
      return this.saleData.infoVenta.venta.map(payment => ({
        method: payment.formaPago || 'Método no especificado',
        amount: parseFloat(payment.montoVenta || 0)
      })).filter(payment => payment.amount > 0);
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    updateFormat() {
      console.log('📄 Formato cambiado a:', this.selectedFormat);
      // Forzar re-renderizado del componente
      this.$forceUpdate();
    },
    
    async downloadPDF() {
      try {
        this.showLoading(`Generando PDF ${this.formatTypeText.toLowerCase()}...`);
        
        // Cargar librería html2pdf si no está disponible
        if (!window.html2pdf) {
          await this.loadHtml2PdfLibrary();
        }
        
        // Delay para asegurar que el DOM esté completamente renderizado
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // CORREGIDO: Capturar específicamente el voucher-container, no todo el print-content
        const printContentElement = this.$refs.printContent;
        if (!printContentElement) {
          throw new Error('No se pudo encontrar el contenedor de impresión');
        }
        
        const voucherContainer = printContentElement.querySelector('.voucher-container');
        if (!voucherContainer) {
          throw new Error('No se pudo encontrar el contenido del comprobante para exportar');
        }
        
        // Validar que los datos de venta están disponibles
        if (!this.saleData || !this.saleData.idTransaccion) {
          throw new Error('Los datos de la venta no están disponibles');
        }
        
        // Debug: verificar contenido del elemento
        console.log('📄 Elemento voucher-container encontrado:', voucherContainer);
        console.log('📄 Contenido HTML del voucher:', voucherContainer.innerHTML.substring(0, 300) + '...');
        console.log('📄 Dimensiones del voucher:', {
          width: voucherContainer.offsetWidth,
          height: voucherContainer.offsetHeight,
          scrollWidth: voucherContainer.scrollWidth,
          scrollHeight: voucherContainer.scrollHeight
        });
        console.log('📄 Formato seleccionado:', this.selectedFormat);
        console.log('📄 Es formato térmico:', this.isThermalFormat);
        console.log('📄 Datos de venta:', this.saleData.idTransaccion);
        console.log('💳 Es multipago:', this.isMultiPago);
        
        const formatSuffix = this.selectedFormat === 'thermal' ? 'termico' : 'carta';
        const fileName = `comprobante_${formatSuffix}_${this.saleData.idTransaccion}_${new Date().toISOString().split('T')[0]}.pdf`;
        
        // Aplicar clase temporal para limpiar los estilos visuales durante exportación
        voucherContainer.classList.add('pdf-export-mode');
        
        // Delay para que los estilos se apliquen completamente
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Debug: verificar dimensiones después de aplicar pdf-export-mode
        console.log('📄 Dimensiones después de pdf-export-mode:', {
          width: voucherContainer.offsetWidth,
          height: voucherContainer.offsetHeight,
          scrollWidth: voucherContainer.scrollWidth,
          scrollHeight: voucherContainer.scrollHeight,
          computedWidth: window.getComputedStyle(voucherContainer).width,
          computedMaxWidth: window.getComputedStyle(voucherContainer).maxWidth
        });
        
        // Configuración según el formato seleccionado
        let options;
        
        if (this.selectedFormat === 'thermal') {
          // Configuración para formato térmico 80mm
          options = {
            margin: [2, 2, 2, 2], // Márgenes mínimos para térmico
            filename: fileName,
            image: { 
              type: 'jpeg', 
              quality: 0.98 
            },
            html2canvas: { 
              scale: 3, // Resolución optimizada para térmico
              useCORS: true, 
              backgroundColor: '#ffffff',
              logging: false,
              allowTaint: false,
              letterRendering: true,
              foreignObjectRendering: false,
              removeContainer: false,
              width: 300, // Forzar ancho de 300px (~80mm)
              height: Math.max(400, voucherContainer.scrollHeight),
              windowWidth: 300,
              windowHeight: Math.max(400, voucherContainer.scrollHeight),
              x: 0,
              y: 0,
              scrollX: 0,
              scrollY: 0,
              onclone: function(clonedDoc) {
                // CORREGIDO: Asegurar que el contenido térmico use todo el ancho
                const clonedVoucher = clonedDoc.querySelector('.voucher-container');
                if (clonedVoucher) {
                  clonedVoucher.style.visibility = 'visible';
                  clonedVoucher.style.opacity = '1';
                  clonedVoucher.style.width = '300px';
                  clonedVoucher.style.maxWidth = '300px';
                  clonedVoucher.style.minWidth = '300px';
                  clonedVoucher.style.margin = '0';
                  clonedVoucher.style.padding = '12px';
                  clonedVoucher.style.boxSizing = 'border-box';
                  clonedVoucher.style.fontSize = '12px';
                  
                  // Asegurar que todo el contenido use el ancho completo
                  const allElements = clonedVoucher.querySelectorAll('*');
                  allElements.forEach(el => {
                    el.style.maxWidth = '100%';
                    el.style.boxSizing = 'border-box';
                  });
                }
              }
            },
            jsPDF: { 
              unit: 'mm', 
              format: [80, Math.max(150, Math.ceil(voucherContainer.scrollHeight * 0.3))], // Altura dinámica ajustada
              orientation: 'portrait',
              compress: true
            }
          };
        } else {
          // Configuración para formato carta
          options = {
            margin: [10, 10, 10, 10],
            filename: fileName,
            image: { 
              type: 'jpeg', 
              quality: 0.95 
            },
            html2canvas: { 
              scale: 3, // Buena resolución para formato carta
              useCORS: true, 
              backgroundColor: '#ffffff',
              logging: false,
              allowTaint: false,
              letterRendering: true,
              foreignObjectRendering: false,
              removeContainer: false,
              width: voucherContainer.scrollWidth,
              height: voucherContainer.scrollHeight,
              windowWidth: voucherContainer.scrollWidth,
              windowHeight: voucherContainer.scrollHeight,
              x: 0,
              y: 0,
              scrollX: 0,
              scrollY: 0,
              onclone: function(clonedDoc) {
                // Asegurar que los estilos de tabla se mantengan en el clon
                const clonedVoucher = clonedDoc.querySelector('.voucher-container');
                if (clonedVoucher) {
                  clonedVoucher.style.visibility = 'visible';
                  clonedVoucher.style.opacity = '1';
                }
              }
            },
            jsPDF: { 
              unit: 'mm', 
              format: 'letter',
              orientation: 'portrait',
              compress: true
            }
          };
        }
        
        console.log('📄 Opciones PDF configuradas:', options);
        console.log('📄 Iniciando captura del elemento...');
        
        // Generar PDF usando específicamente el voucher-container
        await window.html2pdf().set(options).from(voucherContainer).save();
        
        // Restaurar clase original después de la exportación
        setTimeout(() => {
          voucherContainer.classList.remove('pdf-export-mode');
        }, 500);
        
        this.hideLoading();
        const successMessage = this.selectedFormat === 'thermal' 
          ? '¡PDF térmico generado exitosamente! Listo para imprimir en impresora térmica de 80mm.'
          : '¡PDF formato carta generado exitosamente! Listo para imprimir en impresora estándar.';
        this.showSuccess(successMessage);
        
      } catch (error) {
        console.error('❌ Error generando PDF:', error);
        this.hideLoading();
        this.showError('Error al generar PDF', error.message || 'Error desconocido al generar el PDF');
      }
    },
    
    loadHtml2PdfLibrary() {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    showLoading(message) {
      this.isLoading = true;
      this.loadingMessage = message;
    },
    
    hideLoading() {
      this.isLoading = false;
      this.loadingMessage = '';
    },
    
    showSuccess(message) {
      if (this.$swal) {
        this.$swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: message,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          position: 'top-end',
          showConfirmButton: false
        });
      }
    },
    
    showError(title, message) {
      if (this.$swal) {
        this.$swal.fire({
          icon: 'error',
          title: title,
          text: message,
          confirmButtonColor: '#6bd098'
        });
      } else {
        alert(`${title}: ${message}`);
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'No especificada';
      
      try {
        const date = new Date(dateString);
        return date.toLocaleString('es-CL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return 'Fecha inválida';
      }
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0
      }).format(value || 0);
    },
    
    getStatusText(status) {
      if (!status || status === '') return '[VIGENTE]';
      return status;
    },
    
    // Manejador de eventos del teclado
    handleKeyPress(event) {
      // Cerrar modal con Escape
      if (event.key === 'Escape' || event.keyCode === 27) {
        // Prevenir propagación para evitar cerrar otros modales
        event.stopPropagation();
        event.preventDefault();
        console.log('🔑 VoucherPrintModal: Escape presionado - cerrando modal');
        this.closeModal();
      }
    }
  },
  
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          // Cuando se abre el modal de impresión, asegurar que esté por encima
          document.body.style.overflow = 'hidden';
        } else {
          // Cuando se cierra, restaurar scroll solo si no hay otros modales
          if (!document.querySelector('.modal-backdrop')) {
            document.body.style.overflow = '';
          }
        }
      },
      immediate: true
    }
  },
  
  mounted() {
    // Listener para tecla Escape
    document.addEventListener('keydown', this.handleKeyPress);
  },
  
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyPress);
    // Restaurar scroll del body al destruir el componente
    if (!document.querySelector('.modal-backdrop')) {
      document.body.style.overflow = '';
    }
  }
}
</script>

<style scoped>
.print-window-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 10500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  opacity: 0;
  animation: fadeInPrint 0.3s ease-out forwards;
}

.print-window {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

/* Header */
.print-header {
  background: #0d6efd;
  background-image: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-title {
  font-size: 18px;
  font-weight: 600;
}

.header-title i {
  margin-right: 8px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-action {
  background: white;
  background-color: white;
  border: 1px solid #ddd;
  color: #333;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  opacity: 1;
  position: relative;
}

.btn-action:hover {
  background: #f8f9fa;
  background-color: #f8f9fa;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-color: #ccc;
}

/* Estilo específico para el botón de cerrar - ELEGANTE */
.btn-action.close {
  background: #dc3545 !important;
  background-color: #dc3545 !important;
  border: 1px solid #dc3545 !important;
  color: white !important;
  opacity: 1 !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 3px rgba(220, 53, 69, 0.2) !important;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease !important;
  font-size: 13px !important;
  padding: 6px 16px !important;
}

.btn-action.close:hover {
  background: #c82333 !important;
  background-color: #c82333 !important;
  border-color: #bd2130 !important;
  color: white !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3) !important;
}

/* Efectos simplificados */

/* Asegurar que el icono también sea visible */
.btn-action.close i {
  color: white !important;
  opacity: 1 !important;
  font-size: 14px !important;
}

/* Estilo específico para el botón PDF - ELEGANTE */
.btn-action.pdf {
  background: #28a745 !important;
  background-color: #28a745 !important;
  border: 1px solid #28a745 !important;
  color: white !important;
  opacity: 1 !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 3px rgba(40, 167, 69, 0.2) !important;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease !important;
  font-size: 13px !important;
  padding: 6px 16px !important;
}

.btn-action.pdf:hover {
  background: #218838 !important;
  background-color: #218838 !important;
  border-color: #1e7e34 !important;
  color: white !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 2px 6px rgba(40, 167, 69, 0.3) !important;
}

/* Efectos simplificados para PDF */

/* Asegurar que el icono también sea visible */
.btn-action.pdf i {
  color: white !important;
  opacity: 1 !important;
  font-size: 14px !important;
}

/* Estados adicionales para asegurar visibilidad */
.btn-action:focus,
.btn-action:active {
  outline: none !important;
  opacity: 1 !important;
}

.btn-action.close:focus,
.btn-action.close:active {
  background: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2) !important;
  opacity: 1 !important;
}

.btn-action.pdf:focus,
.btn-action.pdf:active {
  background: #28a745 !important;
  box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2) !important;
  opacity: 1 !important;
}

/* Prevenir cualquier efecto de deshabilitado */
.btn-action:disabled,
.btn-action.disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

/* Asegurar que nunca se vea deshabilitado cuando está activo */
.btn-action:not(:disabled):not(.disabled) {
  opacity: 1 !important;
  cursor: pointer !important;
}

/* Mejora de contraste sutil para el botón cerrar */
.btn-action.close {
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
}

/* Clase adicional para asegurar visibilidad del botón cerrar */
.btn-action.close span {
  font-weight: 600 !important;
  font-size: 13px !important;
  color: white !important;
  opacity: 1 !important;
  display: inline-block !important;
}

/* Eliminar animaciones exageradas - mantener diseño sutil */

/* Asegurar buen contraste */
.print-header .btn-action.close {
  filter: brightness(1.05) !important;
}

/* Format Selector */
.format-selector {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 10px;
}

.format-label {
  color: white;
  font-size: 13px;
  font-weight: 500;
}

.format-select {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  min-width: 120px;
}

.format-select:focus {
  outline: none;
  background: white;
  border-color: rgba(255, 255, 255, 0.8);
}

/* Loading */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.loading-content {
  background: white;
  padding: 20px 30px;
  border-radius: 8px;
  text-align: center;
  font-size: 16px;
}

.loading-content i {
  margin-right: 10px;
  color: #0d6efd;
}

/* Content */
.print-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 500px;
}

.voucher-container {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.4;
  color: #333;
  max-width: 302px; /* Optimizado para 80mm térmico */
  margin: 0 auto;
  /* Marco visual para distinguir del fondo */
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
  background-color: #fefefe;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
}

/* Efecto de papel térmico */
.voucher-container::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #f0f0f0 0%, #ffffff 100%);
  border-radius: 6px;
  z-index: -1;
}

/* Líneas perforadas en los bordes para simular papel térmico */
.voucher-container::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -8px;
  width: 4px;
  background: repeating-linear-gradient(
    to bottom,
    #ccc 0px,
    #ccc 3px,
    transparent 3px,
    transparent 6px
  );
  border-radius: 2px;
}

/* Formato térmico específico */
.voucher-container.thermal-format {
  max-width: 302px; /* 80mm */
  border: 2px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 10px;
}

/* Formato carta específico */
.voucher-container.letter-format {
  max-width: 600px; /* Ancho estándar carta */
  border: 1px solid #ddd;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.voucher-container.letter-format::before,
.voucher-container.letter-format::after {
  display: none; /* Sin efectos térmicos en formato carta */
}

/* Tabla Térmica */
.thermal-items {
  margin-bottom: 15px;
}

.thermal-header {
  display: flex;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 6px 3px;
  font-weight: bold;
  font-size: 10px;
  text-align: center;
  justify-content: space-between;
  align-items: center;
}

.th-codigo {
  flex: 0 0 25%;
  text-align: left;
  padding-left: 4px;
}

.th-cant {
  flex: 0 0 15%;
}

.th-precio {
  flex: 0 0 30%;
  text-align: right;
}

.th-total {
  flex: 0 0 30%;
  text-align: right;
  padding-right: 4px;
}

.thermal-item {
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

.thermal-row-data {
  display: flex;
  padding: 4px 3px;
  font-size: 11px;
  border-bottom: 1px dotted #ddd;
  justify-content: space-between;
  align-items: center;
  min-height: 20px;
}

.td-codigo {
  flex: 0 0 25%;
  font-family: monospace;
  font-size: 10px;
  padding-left: 4px;
  word-break: break-all;
}

.td-cant {
  flex: 0 0 15%;
  text-align: center;
  font-weight: bold;
}

.td-precio {
  flex: 0 0 30%;
  text-align: right;
  font-size: 10px;
}

.td-total {
  flex: 0 0 30%;
  text-align: right;
  font-weight: bold;
  padding-right: 4px;
  font-size: 10px;
}

.thermal-row-description {
  padding: 4px 6px 6px 6px;
  font-size: 11px;
  font-weight: normal;
  line-height: 1.3;
  color: #444;
  background-color: #fcfcfc;
  word-wrap: break-word;
  border-bottom: 1px solid #eee;
}

/* Tabla tradicional para formato carta */
.items-table.letter-format {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 15px;
  table-layout: fixed;
}

.items-table.letter-format th,
.items-table.letter-format td {
  border: 1px solid #ddd;
  padding: 10px 8px;
  text-align: center;
  vertical-align: middle;
}

.items-table.letter-format th {
  background-color: #f8f9fa;
  font-weight: bold;
  font-size: 11px;
  text-align: center;
}

/* Alineación específica por columna en formato carta */
.items-table.letter-format th:nth-child(1),
.items-table.letter-format td:nth-child(1) {
  width: 15%;
  text-align: center;
  font-family: monospace;
  font-size: 10px;
}

.items-table.letter-format th:nth-child(2),
.items-table.letter-format td:nth-child(2) {
  width: 40%;
  text-align: left;
  padding-left: 12px;
  word-wrap: break-word;
}

.items-table.letter-format th:nth-child(3),
.items-table.letter-format td:nth-child(3) {
  width: 10%;
  text-align: center;
  font-weight: bold;
}

.items-table.letter-format th:nth-child(4),
.items-table.letter-format td:nth-child(4),
.items-table.letter-format th:nth-child(5),
.items-table.letter-format td:nth-child(5) {
  width: 17.5%;
  text-align: right;
  font-family: monospace;
  font-size: 11px;
  padding-right: 12px;
}

/* Voucher Header */
.voucher-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #333;
}

.company-info h2 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: bold;
}

.company-info p {
  margin: 2px 0;
  font-size: 12px;
}

.document-info {
  text-align: right;
}

.document-info h3 {
  margin: 0 0 5px 0;
  font-size: 16px;
  font-weight: bold;
}

.document-info p {
  margin: 2px 0;
  font-size: 12px;
}

/* Sale Info */
.sale-info {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ddd;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 12px;
}

.info-row .label {
  font-weight: bold;
  color: #555;
}

/* Payment Methods Section */
.payment-methods-section {
  margin-top: 10px;
}

/* Thermal Payment Methods */
.thermal-payments {
  margin-top: 8px;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  background-color: #fafafa;
}

.thermal-payment-header {
  display: flex;
  background-color: #e9ecef;
  padding: 4px 6px;
  font-weight: bold;
  font-size: 10px;
  border-bottom: 1px solid #dee2e6;
}

.payment-method-col {
  flex: 1;
  text-align: left;
}

.payment-amount-col {
  flex: 0 0 35%;
  text-align: right;
}

.thermal-payment-row {
  display: flex;
  padding: 3px 6px;
  font-size: 11px;
  border-bottom: 1px solid #eee;
}

.thermal-payment-row:last-child {
  border-bottom: none;
}

.payment-method {
  flex: 1;
  text-align: left;
  color: #333;
}

.payment-amount {
  flex: 0 0 35%;
  text-align: right;
  font-weight: bold;
  color: #333;
}

/* Letter Format Payment Methods */
.letter-payments {
  margin-top: 10px;
}

.payment-methods-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-bottom: 15px;
  table-layout: fixed;
}

.payment-methods-table th,
.payment-methods-table td {
  border: 1px solid #ddd;
  padding: 8px 10px;
  vertical-align: middle;
}

.payment-methods-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  font-size: 11px;
  color: #000;
}

/* Alineación específica por columna */
.payment-methods-table th:first-child,
.payment-methods-table td:first-child {
  width: 60%;
  text-align: left;
  padding-left: 12px;
}

.payment-methods-table th:last-child,
.payment-methods-table td:last-child {
  width: 40%;
  text-align: right;
  padding-right: 12px;
  font-weight: bold;
  font-family: monospace;
  color: #333;
}

.payment-methods-table th:first-child {
  font-weight: bold;
}

.payment-methods-table th:last-child {
  font-weight: bold;
  text-align: right;
}

/* Items Section */
.items-section h4 {
  text-align: center;
  margin: 15px 0 10px 0;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  border-bottom: 1px solid #333;
  padding-bottom: 5px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-bottom: 15px;
}

.items-table th,
.items-table td {
  border: 1px solid #ddd;
  padding: 4px 2px;
  text-align: center;
}

.items-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  font-size: 10px;
}

.items-table td:nth-child(2) {
  text-align: left;
  max-width: 120px;
  word-wrap: break-word;
}

.info-row .label {
  font-weight: bold;
  width: 120px;
  flex-shrink: 0;
}

.info-row .value {
  flex: 1;
}

/* Items */
.items-section h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  border-bottom: 1px solid #333;
  padding-bottom: 5px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
  font-size: 12px;
}

.items-table th,
.items-table td {
  padding: 5px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.items-table th {
  background: #f5f5f5;
  font-weight: bold;
  border-bottom: 2px solid #333;
}

.items-table td:nth-child(3),
.items-table td:nth-child(4),
.items-table td:nth-child(5),
.items-table th:nth-child(3),
.items-table th:nth-child(4),
.items-table th:nth-child(5) {
  text-align: right;
}

/* Totals */
.totals-section {
  margin-top: 15px;
  padding-top: 10px;
  border-top: 1px solid #333;
}

.total-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
  font-size: 12px;
}

.total-row.final {
  font-weight: bold;
  font-size: 14px;
  border-top: 1px solid #333;
  padding-top: 5px;
  margin-top: 5px;
}

/* Footer */
.voucher-footer {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #333;
  text-align: center;
  font-size: 11px;
}

.voucher-footer p {
  margin: 2px 0;
}

/* Animaciones */
@keyframes fadeInPrint {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Estilos específicos para formato térmico 80mm */
.voucher-container {
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

/* Optimizaciones para impresión térmica */
.voucher-header {
  flex-direction: column !important;
  text-align: center !important;
}

.voucher-header .document-info {
  text-align: center !important;
  margin-top: 10px;
}

.company-info h2 {
  font-size: 16px !important;
}

.document-info h3 {
  font-size: 14px !important;
}

/* Print styles - Ocultar marcos en PDF */
@media print {
  .print-window-overlay {
    background: white !important;
    position: static !important;
    width: auto !important;
    height: auto !important;
    padding: 0 !important;
    animation: none !important;
    opacity: 1 !important;
  }
  
  .voucher-container {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    background-color: white !important;
  }
  
  .voucher-container::before,
  .voucher-container::after {
    display: none !important;
  }
  
  .print-window {
    box-shadow: none !important;
    max-width: none !important;
    max-height: none !important;
    width: 100% !important;
    height: auto !important;
  }
  
  .print-header,
  .loading-overlay {
    display: none !important;
  }
  
  .print-content {
    padding: 0 !important;
    overflow: visible !important;
  }
  
  .voucher-container {
    font-size: 12px !important;
  }
}

/* Clase temporal para exportación PDF - Optimizada para captura */
.voucher-container.pdf-export-mode {
  border: none !important;
  box-shadow: none !important;
  padding: 8px !important;
  background-color: white !important;
  margin: 0 !important;
  position: relative !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  max-width: none !important;
  width: auto !important;
  height: auto !important;
  overflow: visible !important;
  font-family: 'Courier New', monospace !important;
  color: #000 !important;
  line-height: 1.4 !important;
}

.voucher-container.pdf-export-mode::before,
.voucher-container.pdf-export-mode::after {
  display: none !important;
}

/* CORREGIDO: Estilos específicos para formato térmico en PDF */
.voucher-container.pdf-export-mode.thermal-format {
  width: 300px !important; /* Forzar ancho de ~80mm para ocupar todo el espacio */
  max-width: 300px !important;
  min-width: 300px !important;
  margin: 0 !important;
  padding: 12px !important;
  font-size: 12px !important;
  box-sizing: border-box !important;
}

/* Asegurar que el contenido térmico use todo el ancho */
.voucher-container.pdf-export-mode.thermal-format * {
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.voucher-container.pdf-export-mode.thermal-format .voucher-header {
  width: 100% !important;
  margin-bottom: 15px !important;
}

.voucher-container.pdf-export-mode.thermal-format .company-info h2 {
  font-size: 14px !important;
  font-weight: bold !important;
  margin: 0 0 5px 0 !important;
}

.voucher-container.pdf-export-mode.thermal-format .document-info h3 {
  font-size: 12px !important;
  font-weight: bold !important;
  margin: 0 0 5px 0 !important;
}

/* Asegurar que las tablas térmicas usen todo el ancho */
.voucher-container.pdf-export-mode.thermal-format .thermal-items,
.voucher-container.pdf-export-mode.thermal-format .thermal-header,
.voucher-container.pdf-export-mode.thermal-format .thermal-row-data {
  width: 100% !important;
  max-width: 100% !important;
}

.voucher-container.pdf-export-mode.thermal-format .sale-info,
.voucher-container.pdf-export-mode.thermal-format .info-row {
  width: 100% !important;
  max-width: 100% !important;
}

.voucher-container.pdf-export-mode.thermal-format .thermal-payments,
.voucher-container.pdf-export-mode.thermal-format .thermal-payment-header,
.voucher-container.pdf-export-mode.thermal-format .thermal-payment-row {
  width: 100% !important;
  max-width: 100% !important;
}

/* Asegurar que el texto sea legible en formato térmico */
.voucher-container.pdf-export-mode.thermal-format .company-info p,
.voucher-container.pdf-export-mode.thermal-format .document-info p,
.voucher-container.pdf-export-mode.thermal-format .sale-info,
.voucher-container.pdf-export-mode.thermal-format .voucher-footer {
  font-size: 10px !important;
  line-height: 1.3 !important;
  margin: 2px 0 !important;
}

/* Asegurar que todos los elementos internos sean visibles */
.voucher-container.pdf-export-mode * {
  visibility: visible !important;
  opacity: 1 !important;
  color: inherit !important;
}

/* Estilos para payments en modo PDF */
.voucher-container.pdf-export-mode .thermal-payments {
  border: none !important;
  background-color: white !important;
  display: block !important;
}

.voucher-container.pdf-export-mode .thermal-payment-header {
  background-color: #f8f9fa !important;
  display: flex !important;
  padding: 4px 0 !important;
  font-weight: bold !important;
  border-bottom: 1px solid #dee2e6 !important;
}

/* Estilos adicionales para PDF - asegurar visibilidad completa */
.voucher-container.pdf-export-mode .voucher-header {
  display: flex !important;
  justify-content: space-between !important;
  margin-bottom: 20px !important;
  padding-bottom: 15px !important;
  border-bottom: 2px solid #333 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.voucher-container.pdf-export-mode .company-info,
.voucher-container.pdf-export-mode .document-info {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  background: transparent !important;
}

.voucher-container.pdf-export-mode .company-info h2,
.voucher-container.pdf-export-mode .document-info h3 {
  margin: 0 0 8px 0 !important;
  font-weight: bold !important;
  color: #000 !important;
}

.voucher-container.pdf-export-mode .company-info p,
.voucher-container.pdf-export-mode .document-info p {
  margin: 2px 0 !important;
  line-height: 1.3 !important;
  color: #333 !important;
}

.voucher-container.pdf-export-mode .sale-info,
.voucher-container.pdf-export-mode .items-section,
.voucher-container.pdf-export-mode .totals-section,
.voucher-container.pdf-export-mode .voucher-footer {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  background: transparent !important;
  margin-bottom: 15px !important;
}

.voucher-container.pdf-export-mode .info-row {
  display: flex !important;
  justify-content: space-between !important;
  padding: 4px 0 !important;
  border-bottom: 1px dotted #ccc !important;
}

.voucher-container.pdf-export-mode .info-row .label {
  font-weight: bold !important;
  color: #000 !important;
}

.voucher-container.pdf-export-mode .info-row .value {
  color: #333 !important;
  text-align: right !important;
}

.voucher-container.pdf-export-mode .thermal-items,
.voucher-container.pdf-export-mode .thermal-item,
.voucher-container.pdf-export-mode .thermal-row-description {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* CORREGIDO: Mantener flexbox para spacing correcto en térm ico */
.voucher-container.pdf-export-mode .thermal-header,
.voucher-container.pdf-export-mode .thermal-row-data {
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.voucher-container.pdf-export-mode .thermal-header {
  background-color: #f8f9fa !important;
  border: 1px solid #dee2e6 !important;
  padding: 5px 2px !important;
  font-weight: bold !important;
  font-size: 10px !important;
  text-align: center !important;
}

.voucher-container.pdf-export-mode .thermal-row-data {
  padding: 3px 2px !important;
  font-size: 11px !important;
  border-bottom: 1px dotted #ddd !important;
}

/* Asegurar espaciado correcto en térmico para PDF */
.voucher-container.pdf-export-mode .th-codigo,
.voucher-container.pdf-export-mode .td-codigo {
  flex: 0 0 25% !important;
  text-align: left !important;
  padding-left: 4px !important;
  font-family: monospace !important;
  font-size: 10px !important;
}

.voucher-container.pdf-export-mode .th-cant,
.voucher-container.pdf-export-mode .td-cant {
  flex: 0 0 15% !important;
  text-align: center !important;
  font-weight: bold !important;
}

.voucher-container.pdf-export-mode .th-precio,
.voucher-container.pdf-export-mode .td-precio {
  flex: 0 0 30% !important;
  text-align: right !important;
  font-size: 10px !important;
}

.voucher-container.pdf-export-mode .th-total,
.voucher-container.pdf-export-mode .td-total {
  flex: 0 0 30% !important;
  text-align: right !important;
  font-weight: bold !important;
  padding-right: 4px !important;
  font-size: 10px !important;
}

.voucher-container.pdf-export-mode .items-table {
  display: table !important;
  visibility: visible !important;
  opacity: 1 !important;
  width: 100% !important;
  border-collapse: collapse !important;
  font-size: 12px !important;
  margin-bottom: 15px !important;
}

.voucher-container.pdf-export-mode .items-table th,
.voucher-container.pdf-export-mode .items-table td {
  display: table-cell !important;
  visibility: visible !important;
  opacity: 1 !important;
  border: 1px solid #dee2e6 !important;
  padding: 8px 6px !important;
  text-align: center !important;
}

.voucher-container.pdf-export-mode .items-table th {
  background-color: #f8f9fa !important;
  font-weight: bold !important;
  font-size: 11px !important;
}

/* Alineación específica para formato carta en PDF */
.voucher-container.pdf-export-mode .items-table td:nth-child(1) {
  text-align: center !important; /* Código */
  font-family: monospace !important;
  font-size: 10px !important;
}

.voucher-container.pdf-export-mode .items-table td:nth-child(2) {
  text-align: left !important; /* Descripción */
  max-width: 200px !important;
  word-wrap: break-word !important;
  padding-left: 8px !important;
}

.voucher-container.pdf-export-mode .items-table td:nth-child(3) {
  text-align: center !important; /* Cantidad */
  font-weight: bold !important;
}

.voucher-container.pdf-export-mode .items-table td:nth-child(4),
.voucher-container.pdf-export-mode .items-table td:nth-child(5) {
  text-align: right !important; /* Precios */
  font-family: monospace !important;
  font-size: 11px !important;
  padding-right: 8px !important;
}

.voucher-container.pdf-export-mode .payment-methods-table {
  display: table !important;
  visibility: visible !important;
  opacity: 1 !important;
  width: 100% !important;
  border-collapse: collapse !important;
  font-size: 11px !important;
  margin-bottom: 15px !important;
  table-layout: fixed !important;
}

.voucher-container.pdf-export-mode .payment-methods-table th,
.voucher-container.pdf-export-mode .payment-methods-table td {
  display: table-cell !important;
  visibility: visible !important;
  opacity: 1 !important;
  border: 1px solid #dee2e6 !important;
  padding: 8px 10px !important;
  vertical-align: middle !important;
}

.voucher-container.pdf-export-mode .payment-methods-table th {
  background-color: #f8f9fa !important;
  font-weight: bold !important;
  font-size: 11px !important;
  color: #000 !important;
}

/* Alineación específica para tabla de formas de pago */
.voucher-container.pdf-export-mode .payment-methods-table th:first-child,
.voucher-container.pdf-export-mode .payment-methods-table td:first-child {
  width: 60% !important;
  text-align: left !important;
  padding-left: 12px !important;
  font-weight: normal !important;
}

.voucher-container.pdf-export-mode .payment-methods-table th:last-child,
.voucher-container.pdf-export-mode .payment-methods-table td:last-child {
  width: 40% !important;
  text-align: right !important;
  padding-right: 12px !important;
  font-weight: bold !important;
  font-family: monospace !important;
  color: #333 !important;
}

.voucher-container.pdf-export-mode .payment-methods-table th:first-child {
  font-weight: bold !important;
}

.voucher-container.pdf-export-mode .payment-methods-table th:last-child {
  font-weight: bold !important;
  text-align: right !important;
}

/* Responsive */
@media (max-width: 768px) {
  .print-window {
    width: 95%;
    max-height: 95vh;
  }
  
  .print-header {
    padding: 12px 15px;
    flex-direction: column;
    gap: 10px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }
  
  .format-selector {
    margin-right: 0;
    justify-content: space-between;
    width: 100%;
  }
  
  .format-select {
    min-width: 140px;
  }
  
  .voucher-container.letter-format {
    max-width: 100%;
    padding: 15px;
  }
  
  .thermal-header {
    font-size: 9px;
  }
  
  .thermal-row-data {
    font-size: 10px;
  }
  
  .thermal-row-description {
    font-size: 10px;
    padding: 3px 4px;
  }
  
  .thermal-payment-header {
    font-size: 9px;
    padding: 3px 4px;
  }
  
  .thermal-payment-row {
    font-size: 10px;
    padding: 2px 4px;
  }
  
  .payment-methods-table {
    font-size: 10px;
  }
  
  .payment-methods-table th,
  .payment-methods-table td {
    padding: 4px 5px;
  }
  
  .voucher-header {
    flex-direction: column;
    text-align: center;
  }
  
  .document-info {
    text-align: center;
    margin-top: 10px;
  }
}
</style>